import {
  Hint,
  PaymentProcessorEnum,
  PrimaryButton,
  Tooltip,
  getPaymentProcessorTitle,
  toast,
} from '@systemeio/ui'
import { Integration } from 'modules/profile/payment-gateways/components/integration'
import { useConnectedPaymentIntegrations } from 'modules/profile/payment-gateways/hooks/use-connected-payment-integrations'
import { stripeDisconnect } from 'modules/profile/payment-gateways/integrations/stripe/api'
import ApplePayActivateDomainModal from 'modules/profile/payment-gateways/integrations/stripe/components/apple-pay-activate-domain-modal'
import ApplePayDomainList from 'modules/profile/payment-gateways/integrations/stripe/components/apple-pay-domain-list'
import { useApplePayDomains } from 'modules/profile/payment-gateways/integrations/stripe/hooks'
import { StripePaymentIntegrationInterface } from 'modules/profile/payment-gateways/integrations/stripe/types'
import { getStripeHelpPageLink } from 'modules/profile/payment-gateways/integrations/stripe/utils'
import React, { FC, useState } from 'react'
import ConfirmationModal from 'shared/components/confirmation-modal'
import TransLoco from 'shared/components/trans-loco'
import { useLocoTranslation } from 'shared/hooks/use-loco-translation'
import useUser from 'shared/hooks/use-user'
import { BinIcon, StripeLogoIcon } from '@systemeio/ui'

export const Stripe: FC = () => {
  const { user } = useUser()
  const { t } = useLocoTranslation()
  const { getConnectedPaymentIntegration, removePaymentIntegration } =
    useConnectedPaymentIntegrations()

  const [deleteOpened, setDeleteOpened] = useState(false)

  const stripeIntegration = getConnectedPaymentIntegration<StripePaymentIntegrationInterface>({
    processor: PaymentProcessorEnum.Stripe,
  })

  const title = getPaymentProcessorTitle(PaymentProcessorEnum.Stripe)

  const onDelete = async () => {
    await stripeDisconnect()
    await removePaymentIntegration({ processor: PaymentProcessorEnum.Stripe })
    toast.success(
      t('dashboard.settings.payment-gateways.account_removed', {
        payment: title,
      }),
    )
  }

  const { domainList, mutate } = useApplePayDomains({
    shouldFetch: !!stripeIntegration && stripeIntegration.readyToUse,
  })

  return (
    <Integration
      logo={<StripeLogoIcon />}
      additionalInfo={
        <a
          className="primary-link"
          href={getStripeHelpPageLink(user?.dashboardLocale)}
          target="_blank"
          rel="noreferrer"
        >
          <TransLoco
            t={t}
            i18nKey="dashboard.settings.payment_gateways.help_article_label"
            values={{ integration: title }}
          />
        </a>
      }
    >
      {stripeIntegration ? (
        <div className={'flex w-full justify-between gap-3 overflow-hidden'}>
          <div className={'flex flex-col gap-1 truncate'}>
            {!stripeIntegration.readyToUse && (
              <p className={'flex gap-1'}>
                {t('dashboard.settings.payment-gateways.integration_in_progress')}
              </p>
            )}
            <p className={'flex items-center gap-1 text-base lg:text-lg'}>
              <span>{`${t('dashboard.settings.payment-gateways.account_owner_email')}:`}</span>
              <span className={'text-text-primary truncate'}>
                {stripeIntegration.details.accountOwnerEmail}
              </span>
            </p>
            {stripeIntegration.readyToUse && (
              <section className="mt-3">
                <Hint
                  label={
                    <TransLoco
                      t={t}
                      i18nKey={'dashboard.settings.payment_gateways.activate_apple_pay_domain.hint'}
                      components={{
                        link1: (
                          <a
                            className="primary-link"
                            href="https://support.apple.com/en-ie/102775"
                            target={'_blank'}
                            rel={'noreferrer'}
                          />
                        ),
                        link2: (
                          <a
                            className="primary-link"
                            href="https://docs.stripe.com/elements/express-checkout-element#supported-browsers"
                            target={'_blank'}
                            rel={'noreferrer'}
                          />
                        ),
                      }}
                    />
                  }
                >
                  <ApplePayActivateDomainModal afterLeave={mutate} />
                </Hint>
                {!!domainList && !!domainList.length && (
                  <ApplePayDomainList domains={domainList} afterLeave={mutate} />
                )}
              </section>
            )}
          </div>
          <Tooltip
            mode={'hover'}
            label={t('dashboard.settings.payment-gateways.remove_integration', {
              payment: title,
            })}
            wrapperClassName={'flex items-center'}
            className={'h-fit'}
          >
            <button className={'group outline-hidden'} onClick={() => setDeleteOpened(true)}>
              <BinIcon
                className={
                  'group-focus-visible:fill-danger hover:fill-danger main-transition-colors h-[16px] w-[16px]'
                }
              />
            </button>
          </Tooltip>
          <ConfirmationModal
            title={t('dashboard.settings.payment-gateways.remove_integration_description', {
              payment: title,
            })}
            confirmationText={t('dashboard.settings.payment-gateways.remove_payment_text.stripe')}
            opened={deleteOpened}
            onClose={() => setDeleteOpened(false)}
            onConfirm={onDelete}
          />
        </div>
      ) : (
        <PrimaryButton
          type={'button'}
          onClick={() =>
            window.open(
              `/api/dashboard/customer/settings/connected-integrations/${PaymentProcessorEnum.Stripe}/auth`,
              '_self',
            )
          }
          className={'font-bold'}
        >
          {t('dashboard.settings.payment_gateways.connect')}
        </PrimaryButton>
      )}
    </Integration>
  )
}
